/**
 * State income tax for planning: Maryland (state plus county tax), Virginia and the District of
 * Columbia, or a flat effective rate for any other state. Starts from federal AGI with the main
 * state differences (Social Security is exempt in all three). Sources and the review date are
 * listed per state; later years reuse the latest year's rules, except Maryland's standard deduction, which is indexed
 * (and marked estimated) after 2026.
 */
import { Exact } from "../finance/exact";
import { exactOf, wholeDollars, type TaxInputs, type TaxResult } from "./engine";
import type { FilingStatus } from "./tables";

export type StateCode = "MD" | "VA" | "DC" | "flat" | "none";
export type StateInputs = {
  code: StateCode;
  /** Maryland county (local) rate, percent. */
  localRatePct: number;
  /** For "flat": percent of federal AGI. */
  flatRatePct: number;
  /** State-only subtractions, e.g. 529 contributions. */
  subtractions: number;
  /** Pass-through entity tax paid for the owner: added back to state income (the owner takes a credit for it). */
  ptetAddBack?: number;
  /** State income tax withheld for the year; null when not entered (the state estimate then isn't worked out). */
  withholding?: number | null;
};
export const EMPTY_STATE: StateInputs = { code: "MD", localRatePct: 3.2, flatRatePct: 0, subtractions: 0, ptetAddBack: 0, withholding: null };
/** Maryland county (local) income tax rates run from 2.25% to 3.30% (Comptroller of Maryland, local rate table). */
export const MD_LOCAL_RATE_RANGE = { min: 2.25, max: 3.3 } as const;
/**
 * `estimated`: a figure the result depends on is assumed rather than published (Maryland's standard
 * deduction after 2026, or DC's in a year the federal tables are estimated). Shown with the same
 * "estimated tables" badge as the federal figures.
 */
export type StateResult = {
  code: StateCode;
  taxableIncome: number;
  stateTax: number;
  localTax: number;
  surtax: number;
  totalTax: number;
  source: string;
  estimated: boolean;
  /** A current-law note the plan shows with the year (Virginia's standard deduction from 2030). */
  note?: string;
};

type Bracket = [upTo: number, ratePct: number];
/** Tax through a bracket schedule, exactly (rounded once by the caller). */
const tax = (income: number, brackets: Bracket[]): Exact => {
  let t = Exact.int(0);
  let low = 0;
  for (const [hi, rate] of brackets) {
    const band = Math.max(0, Math.min(income, hi) - low);
    t = t.add(exactOf(band).mul(exactOf(rate)).div(Exact.int(100)));
    low = hi;
    if (income <= hi) break;
  }
  return t;
};
/** `pct` percent of an amount, exactly. */
const pctOf = (amount: number, pct: number) => exactOf(amount).mul(exactOf(pct)).div(Exact.int(100));
const joint = (s: FilingStatus) => s === "mfj" || s === "hoh";

// Maryland, from tax year 2025 (Budget Reconciliation and Financing Act of 2025).
const MD_SINGLE: Bracket[] = [
  [1_000, 2],
  [2_000, 3],
  [3_000, 4],
  [100_000, 4.75],
  [125_000, 5],
  [150_000, 5.25],
  [250_000, 5.5],
  [500_000, 5.75],
  [1_000_000, 6.25],
  [Infinity, 6.5],
];
const MD_JOINT: Bracket[] = [
  [1_000, 2],
  [2_000, 3],
  [3_000, 4],
  [150_000, 4.75],
  [175_000, 5],
  [225_000, 5.25],
  [300_000, 5.5],
  [600_000, 5.75],
  [1_200_000, 6.25],
  [Infinity, 6.5],
];
const VA: Bracket[] = [
  [3_000, 2],
  [5_000, 3],
  [17_000, 5],
  [Infinity, 5.75],
];
const DC: Bracket[] = [
  [10_000, 4],
  [40_000, 6],
  [60_000, 6.5],
  [250_000, 8.5],
  [500_000, 9.25],
  [1_000_000, 9.75],
  [Infinity, 10.75],
];

/** Shown on a Virginia plan for 2030 and later, when the standard deduction reverts under current law. */
export const VA_REVERT_NOTE =
  "Virginia's standard deduction returns to $3,000 ($6,000 joint) from 2030 under current law. The General Assembly has extended the higher amounts before and may do so again.";

export const STATE_RULES = {
  MD: {
    name: "Maryland",
    source: "Comptroller of Maryland: 2025 Resident Tax Booklet, Tax Alert on the 2025 legislative session (BRFA 2025), 2026 withholding guide",
    // $3,350 single / dependent / separate and $6,700 joint / head of household / surviving spouse for 2025 and 2026
    // (2025 booklet; Comptroller's 2026 estimated-tax worksheet, owner-verified 2026-10). Indexed for inflation after
    // 2025: later years are estimated from 2026 at the plan's inflation rate, down to the $50, the way the federal
    // tables are (tables.ts tableFor), and marked estimated until the Comptroller publishes them.
    standard: (year: number, inflationPct = 2.5) => {
      if (year <= 2026) return { single: 3_350, joint: 6_700, estimated: false };
      const f = Math.pow(1 + inflationPct / 100, year - 2026);
      const index = (v: number) => Math.floor((v * f) / 50) * 50;
      return { single: index(3_350), joint: index(6_700), estimated: true };
    },
    exemption: 3_200,
    capitalGainsSurtax: { ratePct: 2, agiOver: 350_000 },
  },
  VA: {
    name: "Virginia",
    source: "Virginia Tax: 2025 Form 760 instructions; legislative summary 26-82 (standard deduction 2026 to 2029)",
    // Va. Code §58.1-322.03(1) as amended in the 2026 session (legislative summary 26-82): $8,750 / $17,500 for 2026,
    // $9,200 / $18,400 for 2027, $9,300 / $18,600 for 2028 and 2029. For taxable years after 2029 the deduction
    // reverts to $3,000 single and $6,000 joint, and the plan says so (the General Assembly has extended the
    // higher amounts before, so it may again).
    standard: (year: number): { single: number; joint: number; note?: string } =>
      year >= 2030
        ? { single: 3_000, joint: 6_000, note: VA_REVERT_NOTE }
        : year >= 2028
          ? { single: 9_300, joint: 18_600 }
          : year >= 2027
            ? { single: 9_200, joint: 18_400 }
            : { single: 8_750, joint: 17_500 },
    exemption: 930, exemption65: 800 },
  DC: { name: "District of Columbia", source: "DC Office of Tax and Revenue: 2025 D-40 booklet, 2026 D-40ES booklet", standard: "federal" as const },
};

/** Maryland's personal exemption shrinks as income rises. */
function mdExemption(status: FilingStatus, agi: number): number {
  const full = STATE_RULES.MD.exemption;
  const j = joint(status);
  if (agi <= (j ? 150_000 : 100_000)) return full;
  if (agi <= (j ? 175_000 : 125_000)) return 1_600;
  if (agi <= (j ? 200_000 : 150_000)) return 800;
  return 0;
}

export function stateTax(year: number, inflationPct: number, s: StateInputs, f: TaxInputs, fed: TaxResult): StateResult {
  const status = f.filingStatus;
  const people = (status === "mfj" ? 2 : 1) + f.childrenUnder17 + f.otherDependents;
  // The PTET the business paid is added back; the owner's credit for it means the state total includes it.
  const base = Math.max(0, fed.agi - fed.socialSecurityTaxable - s.subtractions + (s.ptetAddBack ?? 0));
  if (s.code === "none") return { code: "none", taxableIncome: 0, stateTax: 0, localTax: 0, surtax: 0, totalTax: 0, source: "", estimated: false };
  if (s.code === "flat") {
    // A loss year owes nothing; a state never refunds tax on a loss.
    const income = Math.max(0, fed.agi);
    const t = wholeDollars(pctOf(income, s.flatRatePct));
    return { code: "flat", taxableIncome: income, stateTax: t, localTax: 0, surtax: 0, totalTax: t, source: "Flat effective rate entered by the firm", estimated: false };
  }
  if (s.code === "MD") {
    const r = STATE_RULES.MD;
    // Maryland itemizes only when the federal return does.
    const std = r.standard(year, inflationPct);
    const deduction = fed.itemizing ? Math.max(0, fed.itemizedDeductions - fed.saltDeducted) : joint(status) ? std.joint : std.single;
    // The exemption phases out on federal AGI; 65 and older adds $1,000 each.
    const over65 = (status === "mfj" ? f.ages.slice(0, 2) : f.ages.slice(0, 1)).filter((a) => a >= 65).length;
    const taxable = wholeDollars(exactOf(Math.max(0, base - deduction - mdExemption(status, fed.agi) * people - 1_000 * over65)));
    // Each part is rounded once; the total is their sum.
    const st = wholeDollars(tax(taxable, joint(status) ? MD_JOINT : MD_SINGLE));
    const local = wholeDollars(pctOf(taxable, s.localRatePct));
    // Form 502CG starts from the net capital gain after losses and carryovers.
    const gains = Math.max(0, fed.capitalGainOrLoss);
    const surtax = fed.agi > r.capitalGainsSurtax.agiOver ? wholeDollars(pctOf(gains, r.capitalGainsSurtax.ratePct)) : 0;
    return { code: "MD", taxableIncome: taxable, stateTax: st, localTax: local, surtax, totalTax: st + local + surtax, source: r.source, estimated: std.estimated && !fed.itemizing };
  }
  if (s.code === "VA") {
    const r = STATE_RULES.VA;
    const over65 = (status === "mfj" ? f.ages.slice(0, 2) : f.ages.slice(0, 1)).filter((a) => a >= 65).length;
    const std = r.standard(year);
    const deduction = fed.itemizing ? Math.max(0, fed.itemizedDeductions - fed.saltDeducted) : status === "mfj" ? std.joint : std.single;
    const taxable = wholeDollars(exactOf(Math.max(0, base - deduction - r.exemption * people - r.exemption65 * over65)));
    const st = wholeDollars(tax(taxable, VA));
    // The note is about the standard deduction, so a Virginia return that itemizes doesn't carry it.
    return { code: "VA", taxableIncome: taxable, stateTax: st, localTax: 0, surtax: 0, totalTax: st, source: r.source, estimated: false, ...(std.note && !fed.itemizing ? { note: std.note } : {}) };
  }
  // DC follows the federal standard deduction, except that it did not adopt the 2025 OBBBA increase.
  const dcStd = year === 2025 ? ({ single: 15_000, mfj: 30_000, mfs: 15_000, hoh: 22_500 } as const)[status] + (fed.standardDeduction - ({ single: 15_750, mfj: 31_500, mfs: 15_750, hoh: 23_625 } as const)[status]) : fed.standardDeduction;
  const deduction = fed.itemizing ? Math.max(0, fed.itemizedDeductions - fed.saltDeducted) : dcStd;
  const taxable = wholeDollars(exactOf(Math.max(0, base - deduction)));
  const st = wholeDollars(tax(taxable, DC));
  // DC's standard deduction is the federal one, so it is estimated whenever the federal table is.
  return { code: "DC", taxableIncome: taxable, stateTax: st, localTax: 0, surtax: 0, totalTax: st, source: STATE_RULES.DC.source, estimated: fed.tableEstimated && !fed.itemizing };
}
